// Vidéo SKU de la galerie produit — deux comportements, un seul asset.
// Chargé une fois depuis snippets/product-gallery.liquid ; ce fichier n'est jamais
// exécuté deux fois même si la galerie est rendue plusieurs fois sur une page
// (fiche + product-quick-buy), d'où les gardes sur window.

// 1. Modale du carrousel : lecture/pause synchronisée avec l'ouverture de <x-modal>,
//    même comportement que sections/product-video.liquid.
if (!window.__axGalleryVideoModalObserverStarted) {
  window.__axGalleryVideoModalObserverStarted = true;

  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.attributeName !== 'open') continue;

      const modal = mutation.target;
      if (!modal.matches('.ax-gallery-video__modal')) continue;

      const video = modal.querySelector('.ax-gallery-video__player');
      if (!video) continue;

      if (modal.hasAttribute('open')) {
        video.play().catch(() => {});
      } else {
        video.pause();
        video.currentTime = 0;
      }
    }
  }).observe(document.body, { attributes: true, subtree: true, attributeFilter: ['open'] });
}

// 2. Visionneuse plein écran (« Voir plus de visuels ») : PhotoSwipe est images-only côté
//    thème — openLightBox ne collecte que les `:scope > img` et calcule le rang d'ouverture
//    sur les seules cellules data-media-type="image" — et assets/theme.js n'est pas éditable.
//    D'où l'enveloppe sur le prototype du Web Component, API publique. À revérifier après
//    une montée de version Stretch.
if (!window.__axGalleryVideoLightboxPatched) {
  window.__axGalleryVideoLightboxPatched = true;

  // Rang dans la visionneuse, 1-indexé. La galerie produit, elle, garde la vidéo en dernier.
  const AX_VIDEO_POSITION = 4;

  // Toujours par l'id : <x-modal append-to-body> déplace la modale — et donc le lecteur —
  // dans <body>, si bien que la chercher dans la galerie ne marche qu'avant ce déplacement.
  const findPlayer = (gallery) => {
    const modalId = gallery.querySelector('.ax-gallery-video__trigger')?.getAttribute('aria-controls');
    if (!modalId) return null;
    return document.getElementById(modalId)?.querySelector('.ax-gallery-video__player') ?? null;
  };

  // Relu à chaque ouverture : la galerie est re-rendue au changement de variante.
  const readVideo = (gallery) => {
    const player = findPlayer(gallery);
    if (!player) return null;

    const poster = gallery.querySelector('.ax-gallery-video .product-video__poster-image');
    const label = gallery.querySelector('.ax-gallery-video__trigger .sr-only');

    return {
      poster: poster ? poster.currentSrc || poster.src : '',
      playLabel: label ? label.textContent.trim() : '',
      ratio: player.dataset.axRatio,
      sources: [...player.querySelectorAll('source')].map((source) => ({ src: source.src, type: source.type })),
    };
  };

  // PhotoSwipe ne sait afficher que des images : on construit nous-mêmes l'élément.
  const buildSlide = (axVideo) => {
    const wrapper = document.createElement('div');
    wrapper.className = 'ax-lightbox-video';
    // Ratio fourni par Liquid : la boîte est dimensionnée sans attendre les métadonnées.
    if (axVideo.ratio) wrapper.style.setProperty('--ax-lightbox-video-ratio', axVideo.ratio);

    const video = document.createElement('video');
    // Contrôles natifs masqués tant que la lecture n'a pas démarré, comme sur la vignette.
    video.controls = false;
    video.playsInline = true;
    video.preload = 'metadata';
    if (axVideo.poster) video.poster = axVideo.poster;
    axVideo.sources.forEach(({ src, type }) => {
      const source = document.createElement('source');
      source.src = src;
      source.type = type;
      video.appendChild(source);
    });

    // Couvre toute la vidéo : un clic n'importe où lance la lecture, comme sur la vignette.
    const play = document.createElement('button');
    play.type = 'button';
    play.className = 'ax-lightbox-video__play';
    play.addEventListener('click', () => video.play().catch(() => {}));

    const icon = document.createElement('span');
    icon.className = 'product-video__play-icon';

    const label = document.createElement('span');
    label.className = 'sr-only';
    label.textContent = axVideo.playLabel;

    play.append(icon, label);

    // L'overlay disparaît dès la première lecture : il recouvre la vidéo, donc le laisser
    // revenir à la pause bloquerait les contrôles natifs.
    video.addEventListener('play', () => {
      video.controls = true;
      wrapper.setAttribute('data-started', '');
    });

    wrapper.append(video, play);
    return wrapper;
  };

  const patchLightBox = (lightBox, gallery) => {
    if (lightBox.__axVideoPatched) return;
    lightBox.__axVideoPatched = true;

    const loadAndOpen = lightBox.loadAndOpen.bind(lightBox);

    lightBox.loadAndOpen = (openIndex, dataSource) => {
      const axVideo = readVideo(gallery);
      if (!axVideo) return loadAndOpen(openIndex, dataSource);

      const insertAt = Math.min(AX_VIDEO_POSITION - 1, dataSource.length);
      dataSource.splice(insertAt, 0, { axVideo });

      // openIndex est calculé par le thème sur les seules images : tout rang à partir du
      // point d'insertion se décale d'un cran.
      return loadAndOpen(openIndex >= insertAt ? openIndex + 1 : openIndex, dataSource);
    };

    lightBox.on('contentLoad', (event) => {
      const { axVideo } = event.content.data;
      if (!axVideo) return;
      event.preventDefault();
      event.content.element = buildSlide(axVideo);
    });

    // Remet la slide dans son état d'arrivée : coupe le son, rembobine, remasque contrôles
    // et overlay.
    const reset = (wrapper) => {
      const video = wrapper?.querySelector?.('video');
      if (!video) return;
      video.pause();
      video.currentTime = 0;
      video.controls = false;
      wrapper.removeAttribute('data-started');
    };

    lightBox.on('contentDeactivate', (event) => reset(event.content.element));
    lightBox.on('close', () => document.querySelectorAll('.ax-lightbox-video').forEach(reset));
  };

  customElements.whenDefined('product-gallery').then((ProductGallery) => {
    const openLightBox = ProductGallery.prototype.openLightBox;
    if (typeof openLightBox !== 'function') return;

    ProductGallery.prototype.openLightBox = function (index) {
      if (findPlayer(this)) patchLightBox(this.lightBox, this);
      return openLightBox.call(this, index);
    };
  });
}
